import { employeeApi } from '@api/employeeApi';
import { useState, useEffect } from 'react';
import { PiUsersDuotone } from 'react-icons/pi';
import { FaUserShield } from 'react-icons/fa6';
import { cn } from '@helpers/styleUtils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/common/Tabs';
import { ScrollArea } from '@components/common/ScrollArea';
import { Avatar, AvatarFallback, AvatarImage } from '@components/common/Avataar';
import { FiUsers } from 'react-icons/fi';
import { MdOutlineWidgets } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { GlobalLoading } from '@components/loaders/GlobalLoading';

const RoleLists = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const textColors = [
        'text-blue-500',
        'text-red-500',
        'text-green-500',
        'text-purple-500',
        'text-orange-500',
    ];
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await employeeApi.getRoleList();
                setUsers(response);
            } catch (err) {
                console.log(err);
                setError('Failed to fetch user data');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const handleUserClick = user => {
        setSelectedUser(user);
    };

    return (
        <div className="flex h-screen gap-8">
            {loading ? (
                <GlobalLoading />
            ) : error ? (
                <div>{error}</div>
            ) : (
                <div className="w-8/12 ">
                    <div className="bg-white rounded-lg shadow-md py-4">
                        <h2 className="text-xl font-bold mb-4 px-3">Role list</h2>
                        <table className="min-w-full">
                            <thead>
                                <tr className="text-sm text-gray-500">
                                    <th className="px-3 py-2 text-left">Role Name</th>
                                    <th className="px-3 py-2 text-left">Widget Inside</th>
                                    <th className="px-3 py-2 text-left">Users</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users &&
                                    users.length > 0 &&
                                    users.map((user, index) => {
                                        const widgetCount = user?.widget_config ? (
                                            JSON.parse(user.widget_config)?.length || (
                                                <span className="text-red-600">None</span>
                                            )
                                        ) : (
                                            <span className="text-red-600">None</span>
                                        );

                                        return (
                                            <tr
                                                key={user.id}
                                                className="hover:bg-slate-100 cursor-pointer border-b border-gray-100"
                                                onClick={() => handleUserClick(user)}
                                            >
                                                <td className="py-5 px-3 text-sm flex items-center gap-2">
                                                    <FaUserShield
                                                        className={cn('text-lg', textColors[index])}
                                                    />{' '}
                                                    {user.role_name}
                                                </td>
                                                <td className="py-5 px-3">{widgetCount}</td>
                                                <td className="py-5 px-3 text-sm flex items-center gap-2">
                                                    <PiUsersDuotone className="text-lg" />{' '}
                                                    {user.users.length}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="w-4/12">
                <div className="bg-white rounded-lg shadow-md p-6 relative">
                    {selectedUser && selectedUser?.role_name ? (
                        <>
                            <h2 className="text-lg font-bold mb-4">{selectedUser.role_name}</h2>
                            <Tabs defaultValue="main" className="w-full">
                                <TabsList className="w-full bg-unset">
                                    <TabsTrigger
                                        className="w-1/2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary-blue data-[state=active]:rounded-none border-gray-200 border-b-2 rounded-none"
                                        value="main"
                                    >
                                        Main Info
                                    </TabsTrigger>
                                    <TabsTrigger
                                        className="w-1/2 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary-blue data-[state=active]:rounded-none border-gray-200 border-b-2 rounded-none"
                                        value="users"
                                    >
                                        Users
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="main" className=" h-[310px]">
                                    <div className="grid grid-cols-2 gap-2 mb-4">
                                        <div className="flex items-center justify-center flex-col h-14 bg-slate-200 rounded-md">
                                            <div className="text-md font-bold  flex items-center gap-1">
                                                <FiUsers className="text-primary-blue" />
                                                {selectedUser?.users?.length || 0}
                                            </div>
                                            <p className="text-xs text-gray-500">Participants</p>
                                        </div>
                                        <div className="flex items-center justify-center flex-col h-14 bg-slate-200 rounded-md">
                                            <div className="text-md font-bold  flex items-center gap-1">
                                                <MdOutlineWidgets className="text-primary-blue" />
                                                {selectedUser?.widget_config?.length || 0}
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                Widgets variable
                                            </p>
                                        </div>
                                    </div>
                                </TabsContent>
                                <TabsContent value="users" className="bg-slate-100 p-4 h-[310px]">
                                    <ScrollArea className="h-full">
                                        {selectedUser.users.map(user => (
                                            <div
                                                className="flex items-center gap-2 py-2 border-b border-slate-200"
                                                key={user.user_id}
                                            >
                                                <Avatar className="h-6 w-6">
                                                    <AvatarImage src={user.photo} />
                                                    <AvatarFallback>
                                                        {user.user_name.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <p className="text-xs font-semibold">
                                                    {user.user_name}
                                                </p>
                                            </div>
                                        ))}
                                        <div className="h-12"></div>
                                    </ScrollArea>
                                </TabsContent>
                            </Tabs>
                            <div className="absolute left-0 bottom-0 w-full  px-4 py-6 backdrop-blur-sm bg-white/30 shadow-card ">
                                <div
                                    className="shadow-card border text-sm font-bold cursor-pointer hover:bg-gray-200 text-primary-blue border-slate-200 rounded-md w-full bg-white py-2 flex items-center justify-center"
                                    onClick={() =>
                                        navigate(
                                            `/admin/role/${selectedUser.role_name}/edit_widgets`,
                                        )
                                    }
                                >
                                    Edit Widget View
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center p-4 h-[390px]">
                            <h2 className="text-lg font-semibold mb-4">No Role Selected</h2>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export { RoleLists };
